/**
 * Addresses tab (SPEC screen 3). Address PII lives behind its own
 * `partners.party_address.*` grants and is never part of the party
 * collection, so this panel owns its own reads.
 *
 * "Make default" is a plain `is_default: true` PATCH: the server clears the
 * prior default for every overlapping purpose atomically, and the UI
 * reconciles from the refetch — it never sends an "unset the other one" call.
 * Archiving is a DELETE that clears the default flag server-side.
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Pencil, Plus, Star, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { surfaceProblem } from "@/components/form";
import { ApiError } from "@/lib/api/errors";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  ADDRESS_TYPES,
  archivePartyAddress,
  createPartyAddress,
  getPartyAddress,
  updatePartyAddress,
  type AddressType,
  type PartyAddress,
  type PartyAddressCreate,
} from "../api";
import { partnersKeys, usePartyAddresses } from "../queries";

const COUNTRY_RE = /^[A-Z]{2}$/;

interface AddressDraft {
  addressType: AddressType;
  label: string;
  recipientName: string;
  organizationName: string;
  line1: string;
  line2: string;
  district: string;
  city: string;
  region: string;
  postalCode: string;
  countryCode: string;
  phone: string;
  isDefault: boolean;
}

const EMPTY_DRAFT: AddressDraft = {
  addressType: "both",
  label: "",
  recipientName: "",
  organizationName: "",
  line1: "",
  line2: "",
  district: "",
  city: "",
  region: "",
  postalCode: "",
  countryCode: "SA",
  phone: "",
  isDefault: false,
};

function draftFrom(address: PartyAddress): AddressDraft {
  return {
    addressType: address.address_type,
    label: address.label ?? "",
    recipientName: address.recipient_name ?? "",
    organizationName: address.organization_name ?? "",
    line1: address.line1 ?? "",
    line2: address.line2 ?? "",
    district: address.district ?? "",
    city: address.city ?? "",
    region: address.region ?? "",
    postalCode: address.postal_code ?? "",
    countryCode: address.country_code ?? "",
    phone: address.phone ?? "",
    isDefault: address.is_default,
  };
}

export interface AddressesPanelProps {
  partyId: string;
  /** Warn when an enabled customer has no default billing address. */
  isCustomer: boolean;
  partyEditable: boolean;
  canRead?: boolean;
  canCreate?: boolean;
  canUpdate?: boolean;
  canArchive?: boolean;
}

export function AddressesPanel({
  partyId,
  isCustomer,
  partyEditable,
  canRead = true,
  canCreate = true,
  canUpdate = true,
  canArchive = true,
}: AddressesPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [includeArchived, setIncludeArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<{ id: string; etag: string | null } | null>(null);
  const [draft, setDraft] = useState<AddressDraft>(EMPTY_DRAFT);
  const [formError, setFormError] = useState<string | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<PartyAddress | null>(null);

  const addressesQuery = usePartyAddresses(partyId, includeArchived, canRead);
  const addresses = addressesQuery.data ?? [];

  useEffect(() => {
    if (!dialogOpen) {
      setEditing(null);
      setFormError(null);
    }
  }, [dialogOpen]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: partnersKeys.parties.all });
  };

  const loadForEdit = useMutation({
    mutationFn: (addressId: string) => getPartyAddress(partyId, addressId),
    onSuccess: ({ data, etag }) => {
      setEditing({ id: data.id, etag });
      setDraft(draftFrom(data));
      setFormError(null);
      setDialogOpen(true);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const createMutation = useIdempotentMutation<PartyAddress, PartyAddressCreate>({
    mutationFn: (input, key) => createPartyAddress(partyId, input, key),
    onSuccess: () => {
      setDialogOpen(false);
      invalidate();
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const updateMutation = useMutation({
    mutationFn: (input: {
      addressId: string;
      patch: Parameters<typeof updatePartyAddress>[2];
      ifMatch: string;
    }) => updatePartyAddress(partyId, input.addressId, input.patch, input.ifMatch),
    onSuccess: () => {
      setDialogOpen(false);
      invalidate();
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const makeDefaultMutation = useMutation({
    // The row list carries no per-row validator: read the exact entity, then
    // forward its resource-bound ETag. Never synthesize one from list state.
    mutationFn: async (addressId: string) => {
      const { etag } = await getPartyAddress(partyId, addressId);
      if (etag === null) throw new ApiError("Missing address ETag; reload and retry", 428);
      return updatePartyAddress(partyId, addressId, { is_default: true }, etag);
    },
    onSuccess: invalidate,
    onError: (error) => surfaceProblem(error, t),
  });

  const archiveMutation = useMutation({
    mutationFn: (addressId: string) => archivePartyAddress(partyId, addressId),
    onSuccess: () => {
      setArchiveTarget(null);
      invalidate();
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const openCreate = (): void => {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
    setFormError(null);
    setDialogOpen(true);
  };

  const submit = (): void => {
    const country = draft.countryCode.trim().toUpperCase();
    if (draft.line1.trim() === "" || draft.city.trim() === "" || country === "") {
      setFormError(t("partners.addresses.requiredFields"));
      return;
    }
    if (!COUNTRY_RE.test(country)) {
      setFormError(t("partners.addresses.countryInvalid"));
      return;
    }
    setFormError(null);
    const optionals = {
      ...(draft.label.trim() === "" ? {} : { label: draft.label.trim() }),
      ...(draft.recipientName.trim() === "" ? {} : { recipient_name: draft.recipientName.trim() }),
      ...(draft.organizationName.trim() === ""
        ? {}
        : { organization_name: draft.organizationName.trim() }),
      ...(draft.line2.trim() === "" ? {} : { line2: draft.line2.trim() }),
      ...(draft.district.trim() === "" ? {} : { district: draft.district.trim() }),
      ...(draft.region.trim() === "" ? {} : { region: draft.region.trim() }),
      ...(draft.postalCode.trim() === "" ? {} : { postal_code: draft.postalCode.trim() }),
      ...(draft.phone.trim() === "" ? {} : { phone: draft.phone.trim() }),
    };
    if (editing === null) {
      createMutation.mutate({
        address_type: draft.addressType,
        line1: draft.line1.trim(),
        city: draft.city.trim(),
        country_code: country,
        is_default: draft.isDefault,
        ...optionals,
      });
      return;
    }
    if (editing.etag === null) {
      setFormError(t("partners.addresses.requiredFields"));
      return;
    }
    updateMutation.mutate({
      addressId: editing.id,
      ifMatch: editing.etag,
      patch: {
        address_type: draft.addressType,
        line1: draft.line1.trim(),
        city: draft.city.trim(),
        country_code: country,
        is_default: draft.isDefault,
        // Cleared optionals are explicitly nulled — merge-patch treats an
        // omitted key as "leave unchanged", which would strand old PII.
        label: draft.label.trim() === "" ? null : draft.label.trim(),
        recipient_name: draft.recipientName.trim() === "" ? null : draft.recipientName.trim(),
        organization_name:
          draft.organizationName.trim() === "" ? null : draft.organizationName.trim(),
        line2: draft.line2.trim() === "" ? null : draft.line2.trim(),
        district: draft.district.trim() === "" ? null : draft.district.trim(),
        region: draft.region.trim() === "" ? null : draft.region.trim(),
        postal_code: draft.postalCode.trim() === "" ? null : draft.postalCode.trim(),
        phone: draft.phone.trim() === "" ? null : draft.phone.trim(),
      },
    });
  };

  const activeAddresses = addresses.filter((address) => address.archived_at === undefined);
  const missingBilling =
    isCustomer &&
    !activeAddresses.some(
      (address) =>
        address.is_default &&
        (address.address_type === "billing" || address.address_type === "both"),
    );

  const set = <K extends keyof AddressDraft>(key: K, value: AddressDraft[K]): void => {
    setDraft((current) => ({ ...current, [key]: value }));
    setFormError(null);
  };

  if (!canRead) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">
          {t("errors.forbidden", { permission: "partners.party_address.read" })}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-3">
        <CardTitle>
          <h2>{t("partners.addresses.title")}</h2>
        </CardTitle>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="addresses-include-archived"
              checked={includeArchived}
              onCheckedChange={(checked) => setIncludeArchived(checked === true)}
            />
            <Label htmlFor="addresses-include-archived" className="font-normal">
              {t("partners.common.showArchived")}
            </Label>
          </div>
          {canCreate && partyEditable ? (
            <Button type="button" size="sm" onClick={openCreate}>
              <Plus className="size-4" aria-hidden="true" />
              {t("partners.addresses.add")}
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {missingBilling ? (
          <p className="rounded-md border border-warning/40 bg-warning/10 p-2 text-sm">
            {t("partners.addresses.noBillingWarning")}
          </p>
        ) : null}

        {addressesQuery.isError ? (
          <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-destructive">
            <span>{t("partners.addresses.loadError")}</span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void addressesQuery.refetch()}
            >
              {t("actions.retry")}
            </Button>
          </div>
        ) : addresses.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("partners.addresses.empty")}</p>
        ) : (
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {addresses.map((address) => (
              <li
                key={address.id}
                className="flex flex-col gap-2 rounded-md border p-3"
                data-testid="partners-address-card"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">
                    {t(`partners.addressType.${address.address_type}`)}
                  </Badge>
                  {address.is_default ? (
                    <Badge variant="default" className="gap-1">
                      <Star className="size-3" aria-hidden="true" />
                      {t("partners.common.defaultBadge")}
                    </Badge>
                  ) : null}
                  {address.archived_at !== undefined ? (
                    <Badge variant="secondary">{t("partners.common.archivedBadge")}</Badge>
                  ) : null}
                  {address.label !== undefined ? (
                    <span dir="auto" className="text-sm font-medium">
                      {address.label}
                    </span>
                  ) : null}
                </div>
                <address className="flex flex-col text-sm not-italic text-muted-foreground">
                  {[
                    address.recipient_name,
                    address.organization_name,
                    address.line1,
                    address.line2,
                    address.district,
                    address.city,
                    address.region,
                  ]
                    .filter((line): line is string => line !== undefined && line !== "")
                    .map((line, index) => (
                      <span dir="auto" key={`${address.id}-line-${String(index)}`}>
                        {line}
                      </span>
                    ))}
                  {address.postal_code !== undefined || address.country_code !== undefined ? (
                    <span dir="ltr" className="font-mono text-xs">
                      {[address.postal_code, address.country_code].filter(Boolean).join(" ")}
                    </span>
                  ) : null}
                  {address.phone !== undefined ? (
                    <span dir="ltr" className="font-mono text-xs">
                      {address.phone}
                    </span>
                  ) : null}
                </address>
                {partyEditable && address.archived_at === undefined ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {canUpdate ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={loadForEdit.isPending}
                        onClick={() => loadForEdit.mutate(address.id)}
                      >
                        <Pencil className="size-4" aria-hidden="true" />
                        {t("partners.addresses.edit")}
                      </Button>
                    ) : null}
                    {canUpdate && !address.is_default ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={makeDefaultMutation.isPending}
                        onClick={() => makeDefaultMutation.mutate(address.id)}
                      >
                        {t("partners.addresses.makeDefault")}
                      </Button>
                    ) : null}
                    {canArchive ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setArchiveTarget(address)}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                        {t("partners.common.archive")}
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent closeLabel={t("actions.close")} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing === null
                ? t("partners.addresses.createTitle")
                : t("partners.addresses.editTitle")}
            </DialogTitle>
            <DialogDescription>{t("partners.addresses.requiredFields")}</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="address-type">{t("partners.addresses.type")}</Label>
              <Select
                value={draft.addressType}
                onValueChange={(value) => set("addressType", value as AddressType)}
              >
                <SelectTrigger id="address-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADDRESS_TYPES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`partners.addressType.${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="address-label">{t("partners.addresses.label")}</Label>
              <Input
                id="address-label"
                dir="auto"
                value={draft.label}
                onChange={(event) => set("label", event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="address-recipient">{t("partners.addresses.recipient")}</Label>
              <Input
                id="address-recipient"
                dir="auto"
                value={draft.recipientName}
                onChange={(event) => set("recipientName", event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="address-organization">{t("partners.addresses.organization")}</Label>
              <Input
                id="address-organization"
                dir="auto"
                value={draft.organizationName}
                onChange={(event) => set("organizationName", event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="address-line1">{t("partners.addresses.line1")}</Label>
              <Input
                id="address-line1"
                dir="auto"
                value={draft.line1}
                onChange={(event) => set("line1", event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="address-line2">{t("partners.addresses.line2")}</Label>
              <Input
                id="address-line2"
                dir="auto"
                value={draft.line2}
                onChange={(event) => set("line2", event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="address-district">{t("partners.addresses.district")}</Label>
              <Input
                id="address-district"
                dir="auto"
                value={draft.district}
                onChange={(event) => set("district", event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="address-city">{t("partners.addresses.city")}</Label>
              <Input
                id="address-city"
                dir="auto"
                value={draft.city}
                onChange={(event) => set("city", event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="address-region">{t("partners.addresses.region")}</Label>
              <Input
                id="address-region"
                dir="auto"
                value={draft.region}
                onChange={(event) => set("region", event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="address-postal">{t("partners.addresses.postalCode")}</Label>
              <Input
                id="address-postal"
                dir="ltr"
                value={draft.postalCode}
                onChange={(event) => set("postalCode", event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="address-country">{t("partners.addresses.country")}</Label>
              <Input
                id="address-country"
                dir="ltr"
                maxLength={2}
                value={draft.countryCode}
                onChange={(event) => set("countryCode", event.target.value.toUpperCase())}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="address-phone">{t("partners.addresses.phone")}</Label>
              <Input
                id="address-phone"
                dir="ltr"
                value={draft.phone}
                onChange={(event) => set("phone", event.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <Checkbox
                id="address-default"
                checked={draft.isDefault}
                onCheckedChange={(checked) => set("isDefault", checked === true)}
              />
              <Label htmlFor="address-default" className="font-normal">
                {t("partners.addresses.isDefault")}
              </Label>
            </div>
          </div>

          {formError !== null ? (
            <p role="alert" className="text-sm text-destructive">
              {formError}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              loading={createMutation.isPending || updateMutation.isPending}
              onClick={submit}
            >
              {t("actions.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={archiveTarget !== null} onOpenChange={() => setArchiveTarget(null)}>
        <DialogContent closeLabel={t("actions.close")}>
          <DialogHeader>
            <DialogTitle>{t("partners.addresses.archiveTitle")}</DialogTitle>
            <DialogDescription>{t("partners.addresses.archiveBody")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setArchiveTarget(null)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              loading={archiveMutation.isPending}
              onClick={() => {
                if (archiveTarget !== null) archiveMutation.mutate(archiveTarget.id);
              }}
            >
              {t("partners.common.archive")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
